import React, { useState } from "react";

import { getDecimalPlaces } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";
import { getBaseUnits, getOptions, getUnitOptions } from "utils/fields";
import {
    tabList,
    testTypeTabIndex,
    testTypeValidation,
    outsideTempValidation,
    barometricPressureValidation,
} from "utils/airtightness";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Tabs from "components/Tabs";
import mixpanel from "mixpanel-browser";
import TestDataPoints from "./TestDataPoints";

import classes from "./style.module.scss";

const TestData = ({ accessor, change, history }) => {
    const { contentOpen = false, modelId } = useStateSelector(`model`) || {};

    const { id: airTightnessTypeId } = useStateSelector(`${accessor}.testType`) || {};
    const { id: testTypeId } = useStateSelector(`model.modelData.airTightness.testData.testType`) || {};
    const outsideTempValue = useStateSelector(`model.modelData.airTightness.testData.outsideTemp`);
    const outsideTempUnits = useStateSelector(`model.modelData.airTightness.testData.outsideTemp_u`);
    const barometricPressureUnits = useStateSelector(`model.modelData.airTightness.testData.barometricPressure_u`);
    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    // this split().join() handles the initial case when the search string is "?tab=test-data", which confuses the second level of subtabs
    const initialTab = window.location.search.split("?tab=test-data").join("");
    const [activeTab, changeActiveTab] = useState(initialTab);

    const [tabsPresent, setTabsPresent] = useState(testTypeTabIndex[testTypeId] || [tabList[0]]);

    const handleTestTypeChange = (value) => {
        setTabsPresent(testTypeTabIndex[value.id]);
        // Navigate to first tab
        history.push(`/models/${modelId}/airtightness?tab=test-data`);
        changeActiveTab("");
    };

    // 'clear data' button should also clear header values

    return (
        <>
            <div className={classes.airtightness}>
                <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                    <Field
                        component={Select}
                        name={`${accessor}.blowerTest.isCgsbTest`}
                        options={[
                            {
                                label: "CGSB Test",
                                value: true,
                            },
                            {
                                label: "As operated",
                                value: false,
                            },
                        ]}
                        label="Test Conditions"
                        placeholder="Choose Test Conditions"
                        disabled={airTightnessTypeId !== 0}
                    />

                    <Field
                        component={Select}
                        name={`model.modelData.airTightness.testData.testType`}
                        options={getOptions({ fieldKey: "airTestType" })}
                        label="Test Type"
                        placeholder="Choose Test Type"
                        validate={testTypeValidation}
                        onChange={handleTestTypeChange}
                        warning={testTypeId !== 0 ? "blowerDoorVolumeSync" : ""}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`model.modelData.airTightness.testData.outsideTemp`}
                        label="Outside Temperature"
                        validate={outsideTempValidation}
                        decimals={getDecimalPlaces("airTestOutsideTemp")}
                        change={change}
                        setValue={outsideTempValue}
                        units={{
                            base: getBaseUnits("airTestOutsideTemp", modelUnits),
                            options: getUnitOptions("temperature"),
                            selected: outsideTempUnits,
                            unitType: "temperature",
                            accessor: `model.modelData.airTightness.testData.outsideTemp_u`,
                        }}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`model.modelData.airTightness.testData.barometricPressure`}
                        label="Barometric Pressure"
                        validate={barometricPressureValidation}
                        decimals={getDecimalPlaces("airTestBarometricPressure")}
                        units={{
                            base: getBaseUnits("airTestBarometricPressure", modelUnits),
                            options: getUnitOptions("pressure"),
                            selected: barometricPressureUnits,
                            unitType: "pressure",
                            accessor: `model.modelData.airTightness.testData.barometricPressure_u`,
                        }}
                    />
                </InputRow>
                <Tabs
                    type="subTabs"
                    initialTab={""}
                    tabActive={activeTab || ""}
                    onChange={(e) => {
                        history.push(`/models/${modelId}/airtightness?tab=test-data${e}`);
                        mixpanel.track("AirTightness Tab Change", {
                            Tab: e,
                        });

                        return changeActiveTab(e);
                    }}
                    tabs={tabsPresent}
                    className={classes.tabs}
                    stack={contentOpen}
                >
                    {tabsPresent.map((tabData, ind) => {
                        return (
                            <TestDataPoints
                                key={ind}
                                change={change}
                                accessor={`${accessor}.testData.tests.${tabData.testKey}`}
                                testTypeId={testTypeId}
                                testName={tabData.name}
                            />
                        );
                    })}
                </Tabs>
            </div>
        </>
    );
};

export default TestData;
